import React, { useEffect, useState, useRef, useCallback } from 'react';
import './FAQ.css';
import { fetchData } from '../../config/dataService';

function FAQItem({ item, index, isOpen, onToggle }) {
  const itemRef = useRef(null);
  const answerRef = useRef(null);

  // When this item opens, scroll it to the middle of the viewport
  useEffect(() => {
    if (isOpen && itemRef.current) {
      const el = itemRef.current;
      const elRect = el.getBoundingClientRect();
      const elCentre = elRect.top + window.scrollY + elRect.height / 2;
      const targetScrollY = elCentre - window.innerHeight / 2;

      // Small delay lets the answer panel begin expanding before we scroll
      setTimeout(() => {
        window.scrollTo({ top: targetScrollY, behavior: 'smooth' });
      }, 60);
    }
  }, [isOpen]);

  return (
    <div
      ref={itemRef}
      className={`faq-item${isOpen ? ' faq-item--open' : ''}`}
    >
      <button
        className="faq-item__question"
        onClick={() => onToggle(index)}
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${index}`}
        id={`faq-question-${index}`}
      >
        <span className="faq-item__question-text">{item.question}</span>
        <span className="faq-item__arrow" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      <div
        ref={answerRef}
        id={`faq-answer-${index}`}
        role="region"
        aria-labelledby={`faq-question-${index}`}
        className="faq-item__answer-wrap"
        style={{
          maxHeight: isOpen
            ? `${answerRef.current?.scrollHeight || 600}px`
            : '0px'
        }}
      >
        <div className="faq-item__answer">
          {item.answer}
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    fetchData('faq')
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);

  // Toggle: close if already open, open new one and close the previous
  const handleToggle = useCallback((index) => {
    setOpenIndex(prev => (prev === index ? null : index));
  }, []);

  if (loading) return <div className="loading-state">Loading…</div>;
  if (error)   return <div className="error-state">Could not load FAQs. Please try again.</div>;

  const { title = 'Frequently Asked Questions', subtitle, faqs = [] } = data || {};

  return (
    <main className="faq">
      <div className="container">

        <div className="faq__header">
          <h1 className="section-title">{title}</h1>
          {subtitle && <p className="section-subtitle">{subtitle}</p>}
          <div className="divider" />
        </div>

        <div className="faq__list" role="list">
          {faqs.map((item, i) => (
            <FAQItem
              key={i}
              item={item}
              index={i}
              isOpen={openIndex === i}
              onToggle={handleToggle}
            />
          ))}
        </div>

      </div>
    </main>
  );
}
